import * as React from 'react'
import { Form } from 'antd'
import { useImmer } from 'use-immer'
import { get, set, cloneDeep, isEmpty, pick } from 'lodash'
import { setStorageItem, getStorageItem } from 'helpers/localStorage'
import { calculateOrderTotals } from 'helpers/procurement'
import { showError, showClientNotifications } from 'helpers/errors'
import requisitionerFields from 'options/requisitionerFields'
import linkTargets from 'options/linkTargets'
import { t } from 'helpers/i18n'
import Modal from 'elements/Modal'
import Checkbox from 'elements/Checkbox'
import Drawer from 'elements/Drawer'

const getStorageKey = () => 'requisitions.convertToOrder'

const CHECKBOXES = [
  'setQuantityToUnissuedQuantity',
  'addRequisitionDocumentsToOrder',
  'closeRequisitionAfterConvertingToOrder',
  'copyRequisitionCommentsToOrder',
]

const OrderForm = linkTargets.orderRecord.formComponent

function Component(props) {
  const { user, customer, parentRecord, selectedRowKeys } = props

  const selectedRequisitionItems = (parentRecord?.requisitionItems ?? []).filter((each) =>
    selectedRowKeys.includes(each.id)
  )

  const [state, updateState] = useImmer({ modalVisible: true, ...getStorageItem(getStorageKey()) })

  function setState(path, value) {
    updateState((draft) => {
      set(draft, path, value)
    })
  }

  async function handleModalOk() {
    try {
      setState('modalOkButtonLoading', true)

      const responses = await Promise.all([
        props.newOrder(),
        props.populateOrderItems(
          selectedRequisitionItems
            .map((each) => ({
              ...each,
              userName: user.userName,
              quantityOrdered: state.setQuantityToUnissuedQuantity
                ? each.quantity - each.quantityIssued
                : each.quantity,
              supplierId: each.orderSupplierId,
              supplierName: each.orderSupplierName,
              requisitionId: undefined,
              requisitionItemId: undefined,
              quantityIssued: undefined,
            }))
            .filter((each) => each.quantityOrdered > 0)
        ),
      ])

      const item = cloneDeep(responses[0].value.data)

      Object.assign(
        item,
        pick(
          parentRecord,
          requisitionerFields.map((each) => each.key)
        )
      )

      if (state.copyRequisitionCommentsToOrder && !isEmpty(parentRecord.customerComment)) {
        item.comment = `${t('fromRequisition')} ${parentRecord.id}:\n${parentRecord.customerComment}`
      }

      const orderItems = get(responses[1], 'value.data.items', []).map((each) => ({
        ...each,
        source: `REQ${parentRecord.id}`,
        sourceIdNumber: parentRecord.id,
        sourceItemId: each.id,
        id: undefined,
        orderItemId: undefined,
        timeScanned: new Date().toJSON(),
        serverTimeScanned: new Date().toJSON(),
      }))

      const supplierIds = orderItems
        .filter((each) => Boolean(each.supplierId) && each.quantityOrdered > 0)
        .map((each) => each.supplierId)

      const suppliers = await Promise.all(
        [...new Set(supplierIds)].map((each) => props.getSupplier(each).then((r) => r.value.data))
      )

      const purchaseOrderNumbers = await Promise.all(
        supplierIds.map((supplierId) =>
          props.generatePurchaseOrderNumber({ orderId: 0, supplierId }).then((each) => ({
            supplierId,
            ...each.value.data,
          }))
        )
      )

      const orderSuppliers = suppliers.map((each) => ({
        adjustments: 0,
        confirmedDescription: 'No',
        freight: 0,
        purchaseOrderNumber: '',
        shippingInstructions: each.shippingInstructions,
        supplierId: each.id,
        supplierName: each.name,
      }))

      Object.assign(item, calculateOrderTotals({ customer, item, orderItems }))

      orderSuppliers.forEach((each) => {
        try {
          each.purchaseOrderNumber = purchaseOrderNumbers.find(
            (one) => one.supplierId === each.supplierId
          ).purchaseOrderNumber
        } catch (error) {
          console.warn(error)
        }
      })

      const response = await props.saveOrder({
        order: item,
        orderItems: { creating: orderItems },
        orderSuppliers: { creating: orderSuppliers },
      })

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      const order = cloneDeep(response.value.data.items[0])

      if (state.addRequisitionDocumentsToOrder) {
        const copied = await props.copyAllObjectDocuments({
          sourceRequest: {
            domainObjectId: parentRecord.id,
            domainObjectType: 'Requisition',
          },
          destinationRequest: {
            domainObjectId: order.id,
            domainObjectType: 'Order',
          },
        })

        showClientNotifications({ response: copied })

        if (copied.value.data.failureCount > 0) {
          throw new Error()
        }
      }

      updateState((draft) => {
        draft.modalVisible = false
        draft.drawerVisible = true
        draft.drawerSaving = false
        draft.linkTargetRecord = { ...order, orderId: order.id }
      })

      props.onCreateOrder(pick(state, ['closeRequisitionAfterConvertingToOrder']))
    } catch (error) {
      showError({ error })
    } finally {
      setState('modalOkButtonLoading', false)
    }
  }

  function handleOrderFormClose() {
    updateState((draft) => {
      draft.modalVisible = false
      draft.drawerVisible = false
    })

    window.setTimeout(props.onCreatedOrderDrawerClose, 500)
  }

  React.useEffect(() => {
    setStorageItem(
      getStorageKey(),
      CHECKBOXES.reduce((acc, each) => ({ ...acc, [each]: state[each] }), {})
    )
  }, [state])

  return (
    <>
      <Modal
        title={t('convertToOrder')}
        visible={state.modalVisible && !isEmpty(selectedRequisitionItems)}
        okText={t('convert')}
        onOk={handleModalOk}
        okButtonProps={{ loading: state.modalOkButtonLoading }}
        onCancel={props.onCancel}
      >
        {CHECKBOXES.map((key) => (
          <Form.Item key={key}>
            <Checkbox
              checked={get(state, key)}
              onChange={(e) =>
                updateState((draft) => {
                  set(draft, key, e.target.checked)
                })
              }
            >
              {t(key)}
            </Checkbox>
          </Form.Item>
        ))}
      </Modal>
      <Drawer
        title={t('createOrder')}
        size={linkTargets.orderRecord.formSize}
        visible={state.drawerVisible}
        onClose={handleOrderFormClose}
        saving={state.drawerSaving}
      >
        <OrderForm
          linkTargetRecord={state.linkTargetRecord}
          onSave={(pending) => setState('drawerSaving', pending)}
          onSaveAndClose={handleOrderFormClose}
          onCancel={handleOrderFormClose}
        />
      </Drawer>
    </>
  )
}

export default Component
